import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Toast, type ToastType } from "@legal-500/legal500-design-system";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  researchCms,
  fetchAllEditionBpas,
  fetchAllEditions,
  type EditorialContentCreateRequest,
  type EditorialContentDetail,
  type EditorialCommentary,
  type TranslationTarget,
  type TranslationsView,
} from "@/api/researchCms.service";
import { offices, contacts, companies } from "@/api/addressBook.service";
import { docParsing } from "@/api/docParsing.service";
import { CmsEmptyState } from "@/components/ResearchCms";
import { FormProgressBar } from "@/components/ResearchCms/FormProgressBar/FormProgressBar";
import { UnsavedChangesModal } from "@/components/ResearchCms/UnsavedChangesModal/UnsavedChangesModal";
import { MultipleFirmTagsModal } from "@/components/ResearchCms/MultipleFirmTagsModal/MultipleFirmTagsModal";
import { UnscoredQuestionsModal } from "@/components/ResearchCms/UnscoredQuestionsModal/UnscoredQuestionsModal";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { ROUTES } from "../../../routes";
import { CmsLayout } from "./components/CmsLayout";
import { AutoTranslatedNotice } from "./components/AutoTranslatedNotice";
import { SourceDataPanel } from "./components/SourceDataPanel/SourceDataPanel";
import type { SourceDataSource } from "./components/SourceDataPanel/SourceDataPanel";
import { IndividualBanner } from "./components/IndividualBanner/IndividualBanner";
import { TemplateSubmissionTab } from "./components/SubmissionTab/TemplateSubmissionTab";
import { EditorialTab } from "./components/EditorialTab/EditorialTab";
import { IndividualDetailsTab } from "./components/IndividualDetailsTab/IndividualDetailsTab";
import { RankingsTab } from "./components/RankingsTab/RankingsTab";
import { GATE_MESSAGE, TranslationsTab } from "./components/TranslationsTab/TranslationsTab";
import { orgFormResolver, type OrgFormValues } from "./schemas/orgViewSchemas";
import {
  EMPTY_ORG_FORM,
  mergeAdoptedValues,
  toOrgFormValues,
  toUpdateRequest,
  toCommentaryFormValues,
  toCommentaryRequest,
  commentaryLanguage,
  hasCommentaryContent,
  hasSubmissionContent,
  type RecordedSarsScores,
} from "./utils/orgDetailMapper";
import {
  selectSarsSliders,
  planSarsWrite,
  mergeSarScores,
  type SarsSlider,
  type SarsWritePlan,
} from "./utils/sarsFields";
import { resolveLawyerCreateRequest } from "./utils/lawyerCreateRequest";
import { toTranslationsFormValues, translationsSaveBody } from "./utils/translationsForm";
import { findFirmTagsToConfirm } from "./utils/taggedFirms";
import { firmSubmissionTable } from "./utils/firmSubmissionTable";
import { rankingTableStamp } from "./utils/rankingTableStamp";
import {
  findUnsavableRankingEdits,
  UNSAVABLE_RANKING_MESSAGE,
} from "./utils/unsavableRankingEdits";
import { STATUS_CHANGE_MESSAGES } from "./utils/statusChangeMessages";
import { canSetReviewStatus } from "./utils/reviewStatusPermission";
import { filterIndividualFields } from "./utils/filterFieldsByContext";
import { resolveEffectiveTemplateType } from "./utils/resolveTemplateType";
import { individualSourceDataFirm } from "./utils/sourceDataFirm";
import { withSaveTimeout } from "./saveRequestTimeout";
import { useNavigationGuard } from "./hooks/useNavigationGuard";
import type { CmsNavState, EditorialReviewStatus, EditorialWriteStatus } from "./types";
import shared from "./components/cmsShared.module.css";
import styles from "./CmsOrgPage.module.css";

const EMPTY_SLIDERS: SarsSlider[] = [];

const TABS = [
  { id: "rankings", label: "Rankings" },
  { id: "submission", label: "Submission" },
  { id: "editorial", label: "Editorial" },
  { id: "translations", label: "Translations" },
  { id: "details", label: "Details" },
] as const;

const TRANSLATION_GATE_STATUSES: ReadonlyArray<EditorialReviewStatus> = [
  "ready_for_translation",
  "ready_for_release",
];

const NO_EDITION_BPA_MESSAGE =
  "This practice area is not linked to the edition, so commentary cannot be saved.";

function isConflict(err: unknown): boolean {
  return err instanceof Error && (err as Error & { status?: number }).status === 409;
}

/** The conflict names the existing row inline: "... (id: <uuid>, reviewStatus: pending)". */
function conflictContentId(err: unknown): string | null {
  const match = err instanceof Error ? err.message.match(/id: ([0-9a-f-]{36})/i) : null;
  return match ? match[1] : null;
}

async function findLawyerContentId(params: {
  editionId: string;
  bpaId: string;
  contactUuid: string;
  rankingTableId?: string;
}): Promise<string | null> {
  const filters = {
    editionId: params.editionId,
    bpaId: params.bpaId,
    contactId: params.contactUuid,
    rankingTableId: params.rankingTableId,
    limit: 1,
  };
  const list = await researchCms.getEditorialContentList(filters);
  if (list.editorialContent[0]) return list.editorialContent[0].id;
  // The list hides deleted rows unless the status is named, so an empty result is re-checked:
  // an unseen deleted row would read as absent and offer to create a duplicate the API refuses.
  const deleted = await researchCms.getEditorialContentList({
    ...filters,
    status: "deleted",
  });
  return deleted.editorialContent[0]?.id ?? null;
}

async function createOrAdoptLawyerContent(
  request: EditorialContentCreateRequest,
): Promise<EditorialContentDetail> {
  try {
    const created = await withSaveTimeout(() => researchCms.createEditorialContent(request));
    return created.editorialContent;
  } catch (err) {
    if (!isConflict(err)) throw err;
    // Another researcher created the row first; adopt it so the typed values still land.
    const existingId =
      conflictContentId(err) ??
      (await findLawyerContentId({
        editionId: request.editionId,
        bpaId: request.researchBpaId,
        contactUuid: request.contactId ?? "",
        rankingTableId: request.rankingTableId,
      }));
    if (!existingId) throw err;
    return (await researchCms.getEditorialContent(existingId)).editorialContent;
  }
}

const PUBLICATIONS_QUERY = {
  queryKey: ["research-publications-list"],
  queryFn: async () => (await researchCms.getPublications()).publications,
};

export function CmsIndividualPage() {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const {
    editionId: routeEditionId,
    bpaId: routeBpaId,
    contactUuid: routeContactUuid,
  } = useParams<{
    editionId: string;
    bpaId: string;
    contactUuid: string;
  }>();
  const location = useLocation();
  const navState = location.state as CmsNavState;

  const editionId = routeEditionId!;
  const bpaId = routeBpaId!;
  const contactUuid = routeContactUuid!;

  const hasNavState = Boolean(navState?.bpaName);

  const { data: apiData, isLoading: apiLoading } = useQuery({
    queryKey: ["research-individual-api-data", editionId, bpaId],
    queryFn: async () => {
      const [allBpas, allEditions] = await Promise.all([
        fetchAllEditionBpas({ editionId }),
        fetchAllEditions(),
      ]);

      const bpa = allBpas.find(b => b.bpaId === bpaId);
      const edition = allEditions.find(e => e.editionId === editionId);

      const prevConfigId = edition?.previousEditionId ?? null;
      const prevConfig = prevConfigId ? allEditions.find(e => e.id === prevConfigId) : null;

      return {
        bpaName: bpa?.bpaName ?? null,
        rankingEntityId: bpa?.rankingEntityId ?? null,
        rankingEntityName: bpa?.rankingEntityName ?? null,
        rankingEntityType: bpa?.rankingEntityType ?? null,
        editionName: edition?.editionName ?? null,
        directoryId: edition?.directoryId ?? null,
        editionStatus: edition?.status ?? null,
        previousEditionId: prevConfig?.editionId ?? null,
        allEditions,
      };
    },
  });

  const bpaName = navState?.bpaName ?? apiData?.bpaName ?? "";
  const rankingEntityId = navState?.rankingEntityId ?? apiData?.rankingEntityId ?? "";
  const rankingEntityName = navState?.rankingEntityName ?? apiData?.rankingEntityName ?? "";
  const rankingEntityType = navState?.rankingEntityType ?? apiData?.rankingEntityType ?? null;
  const editionName = navState?.editionName ?? apiData?.editionName ?? "";

  const {
    data: rankingTablesData,
    isLoading: rankingTablesLoading,
    isError: rankingTablesFailed,
  } = useQuery({
    queryKey: ["research-ranking-tables", bpaId, editionId],
    queryFn: () => researchCms.getRankingTables({ bpaId, editionId }),
  });

  const hasLawyerSubmission =
    rankingTablesData?.rankingTables.some(t => t.submissionType === "lawyer") ?? false;
  const lawyerTables = rankingTablesData?.rankingTables.filter(t => t.isLawyerTable === 1) ?? [];
  const companyUuid = navState?.companyUuid;
  const organisationName = navState?.organisationName;

  const { data: allContentRows } = useQuery({
    queryKey: ["research-individual-all-content", editionId, bpaId, contactUuid],
    queryFn: async () => {
      const result = await researchCms.getEditorialContentList({
        editionId,
        bpaId,
        contactId: contactUuid,
        limit: 50,
      });
      return result.editorialContent;
    },
  });

  const lawyerSubmissionTables = useMemo(
    () => (rankingTablesData?.rankingTables ?? []).filter(t => t.submissionType === "lawyer"),
    [rankingTablesData],
  );

  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [manualTabsOpened, setManualTabsOpened] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedTableId(null);
  }, [bpaId, editionId]);

  // Derived rather than set by an effect, so the first render already holds the default table
  // and the detail query never fires for an untabled row it is about to throw away.
  const effectiveTableId = selectedTableId ?? lawyerSubmissionTables[0]?.id ?? null;
  const rankingTableId = effectiveTableId ?? undefined;
  const selectedTableName =
    lawyerSubmissionTables.find(t => t.id === effectiveTableId)?.name ?? null;

  const prevEditionId = apiData?.previousEditionId ?? null;

  const { data: previousTiersData } = useQuery({
    queryKey: ["research-prev-lawyer-rankings", bpaId, prevEditionId, contactUuid],
    queryFn: async () => {
      const prevTables = await researchCms.getRankingTables({
        bpaId,
        editionId: prevEditionId!,
      });
      const prevLawyerTables = prevTables.rankingTables.filter(t => t.isLawyerTable === 1);

      const results = await Promise.all(
        prevLawyerTables.map(async table => {
          const data = await researchCms.getLawyerRankings(table.id, {
            contactId: contactUuid,
          });
          const ranking = "lawyerRankings" in data ? (data.lawyerRankings[0] ?? null) : null;
          return {
            name: table.name,
            subtype: table.subtype,
            tier: ranking?.rankingTier ?? null,
          };
        }),
      );

      const map: Record<string, number | null> = {};
      for (const prev of results) {
        const currentTable = lawyerTables.find(
          t => t.name === prev.name && t.subtype === prev.subtype,
        );
        if (currentTable) map[currentTable.id] = prev.tier;
      }
      return map;
    },
    enabled: lawyerTables.length > 0 && prevEditionId !== null,
    staleTime: 300_000,
  });

  const previousTiers = previousTiersData ?? {};

  const [searchParams, setSearchParams] = useSearchParams();

  const { data: scopedRankingData } = useQuery({
    queryKey: ["research-scoped-lawyer-ranking", rankingTableId, contactUuid],
    queryFn: () =>
      researchCms.getLawyerRankings(rankingTableId!, {
        contactId: contactUuid,
      }),
    enabled: rankingTableId !== undefined,
  });
  const scopedRankingId =
    scopedRankingData && "lawyerRankings" in scopedRankingData
      ? (scopedRankingData.lawyerRankings[0]?.id ?? null)
      : null;

  const {
    data: lawyerRankingsMap,
    isFetching: isFetchingRankings,
    refetch: refetchLawyerRankings,
  } = useQuery({
    queryKey: ["research-lawyer-rankings-form", bpaId, editionId, contactUuid],
    queryFn: async () => {
      const results = await Promise.all(
        lawyerTables.map(async table => {
          const data = await researchCms.getLawyerRankings(table.id, {
            contactId: contactUuid,
          });
          const ranking = "lawyerRankings" in data ? (data.lawyerRankings[0] ?? null) : null;
          return [table.id, ranking] as const;
        }),
      );
      const map: OrgFormValues["rankings"] = {};
      for (const [tableId, ranking] of results) {
        map[tableId] = {
          rankingId: ranking?.id ?? null,
          tier: ranking?.rankingTier ?? null,
          status: (ranking?.status as "draft" | "pending" | "published" | "archived") ?? "draft",
          ranked: ranking !== null,
          comment: ranking?.rankingComment ?? "",
          displayOrder: ranking?.displayOrder ?? 0,
        };
      }
      return map;
    },
    enabled: lawyerTables.length > 0,
  });

  const defaultTab = "rankings";
  const activeTab = searchParams.get("tab") ?? defaultTab;
  const setActiveTab = useCallback(
    (tab: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set("tab", tab);
          return next;
        },
        { state: navState },
      );
    },
    [setSearchParams, navState],
  );

  const {
    data,
    error,
    isFetching: isFetchingDetail,
    refetch,
  } = useQuery({
    queryKey: ["research-individual-detail", editionId, bpaId, contactUuid, rankingTableId],
    queryFn: async () => {
      const [list, editionBpas] = await Promise.all([
        researchCms.getEditorialContentList({
          editionId,
          bpaId,
          contactId: contactUuid,
          rankingTableId,
          limit: 1,
        }),
        researchCms.getEditionBpas({ editionId, bpaId, limit: 1 }),
      ]);
      const editionBpaId = editionBpas.editionBpas[0]?.id ?? null;
      // The list hides deleted rows unless the status is named, so an empty result is re-checked:
      // an unseen deleted row would otherwise read as a lawyer with no content at all.
      const deletedMatch = list.editorialContent[0]
        ? null
        : await researchCms.getEditorialContentList({
            editionId,
            bpaId,
            contactId: contactUuid,
            rankingTableId,
            status: "deleted",
            limit: 1,
          });
      const row = list.editorialContent[0] ?? deletedMatch?.editorialContent[0];
      const detail = row ? (await researchCms.getEditorialContent(row.id)).editorialContent : null;
      return { detail, editionBpaId };
    },
    // The table is part of the lookup, so it waits for the table list. A practice area with no
    // lawyer submission table still runs it, untabled.
    enabled: !rankingTablesLoading,
  });

  const detail = data?.detail ?? null;
  const editionBpaId = data?.editionBpaId ?? null;

  const hasLoaded = data !== undefined;
  // `data` is null until the lookup resolves, so the absent-row notice needs the loaded object
  // rather than a falsy check, or it flashes on every load.
  const hasNoContent = data !== undefined && data.detail === null;
  const isDeleted = detail?.reviewStatus === "deleted";

  const { data: fileUrlData } = useQuery({
    queryKey: ["submission-file-url", detail?.docParseResultId],
    queryFn: () => docParsing.getFileUrl(detail!.docParseResultId!),
    enabled: detail?.docParseResultId != null,
  });

  const contentId = detail?.id ?? null;
  const {
    data: sarsData,
    isFetching: isFetchingSars,
    isLoading: sarsLoading,
    refetch: refetchSars,
  } = useQuery({
    queryKey: ["research-sars", contentId],
    queryFn: async () => {
      if (!contentId) return null;
      return researchCms.getSars(contentId);
    },
    enabled: contentId !== null,
  });
  const recordedSarsScores: RecordedSarsScores | null = sarsData?.sars ?? null;

  const templateType = hasNoContent
    ? (navState?.templateType ?? null)
    : (sarsData?.templateType ?? null);
  // Bar practice areas within a conventional publication need bar template fields.
  const effectiveTemplateType = resolveEffectiveTemplateType(rankingEntityType, templateType);

  // One query feeds the SARs sliders, the submission field list and the clerks gate.
  // When the ranking entity is a bar, the effective type overrides the publication's own
  // template so the page fetches bar-specific fields instead.
  const { data: templateFieldsData, isLoading: templateFieldsLoading } = useQuery({
    queryKey: ["research-template-fields", effectiveTemplateType],
    queryFn: async () => {
      if (!effectiveTemplateType) return null;
      const config = await researchCms.getTemplateFields(effectiveTemplateType);
      return config.templateFields;
    },
    enabled: effectiveTemplateType !== null,
  });

  const templateFields = Array.isArray(templateFieldsData) ? templateFieldsData : [];
  // Until the template type and its fields settle, the submission tab would fall back to its
  // hardcoded layout and then remount every input when the template arrives.
  const templateFieldsPending =
    !hasLoaded || (contentId !== null && sarsLoading) || templateFieldsLoading;

  const sarsSliders = useMemo(() => {
    if (templateFields.length === 0) return EMPTY_SLIDERS;
    const individual = selectSarsSliders(templateFields, "individual");
    return individual.length > 0 ? individual : selectSarsSliders(templateFields, "organisation");
  }, [templateFields]);

  const submissionFields = useMemo(
    () =>
      filterIndividualFields(templateFields).filter(
        f => f.fieldTab === "submission" && f.editable === 1,
      ),
    [templateFields],
  );

  const showClerksCommentary = useMemo(
    () => templateFields.some(f => f.fieldName === "bar_editorial_clerks"),
    [templateFields],
  );

  const { data: officeData } = useQuery({
    queryKey: ["research-office", detail?.officeId],
    queryFn: async () => {
      const res = await offices.getById(detail!.officeId!);
      return res.data;
    },
    enabled: Boolean(detail?.officeId),
  });

  // No `detail` means no editorial row; resolve the firm through the address book instead.
  // This also tells the page whether creation is possible.
  const { data: firmLookup, error: firmLookupError } = useQuery({
    queryKey: ["research-firm-lookup", contactUuid],
    queryFn: async () => {
      const contactRes = await contacts.getById(contactUuid);
      const companyId = contactRes.data.companyId;
      const companyRes = companyId ? await companies.getById(companyId) : null;
      return {
        companyId,
        companyName: companyRes?.data.name ?? null,
        officeId: contactRes.data.officeId,
        officeName: contactRes.data.officeName,
        contactName: contactRes.data.name,
      };
    },
    enabled: hasNoContent,
  });

  // Keyed by edition BPA + firm + lawyer, so a ranked-only lawyer with no content row still has it.
  const companyIdForCommentary = detail?.companyId ?? firmLookup?.companyId ?? companyUuid ?? null;

  const {
    data: firmContentRows,
    isPending: firmContentRowsPending,
    isError: firmContentRowsFailed,
  } = useQuery({
    queryKey: ["research-org-all-content", editionId, bpaId, companyIdForCommentary],
    queryFn: async () => {
      const result = await researchCms.getEditorialContentList({
        editionId,
        bpaId,
        companyId: companyIdForCommentary!,
        level: "organisation",
        limit: 50,
      });
      return result.editorialContent;
    },
    enabled: companyIdForCommentary !== null && hasFlag("research-cms-1-enabled"),
  });

  const firmContentTableIds = new Set(
    (firmContentRows ?? []).flatMap(row => (row.rankingTableId ? [row.rankingTableId] : [])),
  );
  const firmTable = firmSubmissionTable({
    rankingTables: rankingTablesData?.rankingTables,
    contentTableIds: firmContentTableIds,
  });

  const {
    data: commentaryData,
    isFetching: isFetchingCommentary,
    refetch: refetchCommentary,
  } = useQuery({
    queryKey: [
      "research-individual-commentary",
      editionBpaId,
      companyIdForCommentary,
      contactUuid,
      detail?.languageIso ?? null,
    ],
    queryFn: async () => {
      if (!editionBpaId || !companyIdForCommentary) return null;
      const publications = apiData?.directoryId
        ? await queryClient.ensureQueryData(PUBLICATIONS_QUERY)
        : undefined;
      return researchCms.getEditorialCommentary(editionBpaId, {
        companyId: companyIdForCommentary,
        contactId: contactUuid,
        languageIso: commentaryLanguage(
          detail,
          publications?.find(pub => pub.directoryId === apiData?.directoryId),
        ),
      });
    },
    enabled: editionBpaId !== null && companyIdForCommentary !== null && data !== undefined,
  });
  const loadedCommentary = commentaryData ?? null;

  const refetchContentAndScores = useCallback(async () => {
    await Promise.all([refetch(), refetchSars(), refetchCommentary()]);
  }, [refetch, refetchSars, refetchCommentary]);

  // Address book first, then router state, then bare uuid. `navState` sits third because
  // the list page may carry the uuid itself as `entityName`. `||` so blanks fall through.
  const lawyerName =
    detail?.contactName?.trim() ||
    firmLookup?.contactName?.trim() ||
    navState?.entityName?.trim() ||
    contactUuid;

  // Snapshotted onto new lawyer rankings; the BPA lawyer list and table viewer read the firm from there.
  const rankingFirmSnapshot = {
    companyId: detail?.companyId || firmLookup?.companyId || companyUuid || undefined,
    firmName: detail?.firmName || firmLookup?.companyName || organisationName || undefined,
    officeId: detail?.officeId || firmLookup?.officeId || undefined,
    officeName: detail?.officeName || officeData?.name || firmLookup?.officeName || undefined,
  };

  const canEdit = hasPermission("staff.research.edit");
  const canApprove = hasPermission("staff.research.approve");
  const canCreate = hasPermission("staff.research.create");
  // Creating a lawyer's first row is POST-then-PATCH, so it needs both create and edit grants.
  // The update path stays on `canEdit` alone so edit-only users can save existing records.
  const canCreateManualEntry = canCreate && canEdit;

  const directoryId = apiData?.directoryId ?? null;
  const editionStatus = apiData?.editionStatus ?? null;

  const stubContentForDetails: EditorialContentDetail = {
    id: "",
    contactId: contactUuid,
    companyId: companyUuid ?? "",
    firmName: organisationName ?? null,
    contactName: null,
    officeId: null,
    officeName: null,
    directoryId: directoryId ?? "",
    editionId,
    researchBpaId: bpaId,
    rankingTableId: null,
    docParseResultId: null,
    fileUri: null,
    sourceContentId: null,
    languageIso: "en",
    detectedLanguage: null,
    teamSizePartners: null,
    teamSizeLawyers: null,
    source: "manual_entry",
    reviewStatus: "pending",
    reviewedBy: null,
    reviewedAt: null,
    createdBy: null,
    updatedBy: null,
    createdAt: "",
    updatedAt: "",
    clients: [],
    contacts: [],
    matters: [],
  };

  const rankingsMetadata = useMemo(
    () =>
      lawyerTables.map(table => ({
        rankingTableId: table.id,
        rankingTableName: table.name,
        rankingId: lawyerRankingsMap?.[table.id]?.rankingId ?? null,
      })),
    [lawyerTables, lawyerRankingsMap],
  );

  // Keyed to match CmsPublicationPage; ResearchCmsHome's key holds a different shape
  // so sharing it would serve the wrong data after navigating from the home page.
  const { data: publications } = useQuery(PUBLICATIONS_QUERY);
  const publication = publications?.find(pub => pub.directoryId === directoryId);
  // The translation step only applies when a publication is written in one language and published
  // in another; an unresolved publication reads as no translation.
  const needsTranslation =
    publication !== undefined && publication.inputLanguage !== publication.outputLanguage;
  const translationsEnabled = needsTranslation && hasFlag("research-cms-1-enabled");
  const firmCreateResult = resolveLawyerCreateRequest(
    hasNoContent && firmLookup
      ? {
          contactUuid,
          contactName: firmLookup.contactName,
          companyId: firmLookup.companyId,
          firmName: firmLookup.companyName,
          officeId: firmLookup.officeId,
          officeName: firmLookup.officeName,
          editionId,
          researchBpaId: bpaId,
          directoryId,
          rankingTableId,
        }
      : null,
    hasNoContent && Boolean(firmLookupError),
  );
  const canCreateLawyerContent = canCreateManualEntry && firmCreateResult?.ok === true;
  const isReadOnly = isDeleted || (hasNoContent && !canCreateLawyerContent);

  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [createdContent, setCreatedContent] = useState<EditorialContentDetail | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const [unscoredLabels, setUnscoredLabels] = useState<string[] | null>(null);
  const [taggedFirmNames, setTaggedFirmNames] = useState<string[] | null>(null);
  const [pendingValues, setPendingValues] = useState<OrgFormValues | null>(null);
  const isBusy = isSaving || isTransitioning;

  const [savedCommentary, setSavedCommentary] = useState<EditorialCommentary | null>(null);
  const commentary = savedCommentary ?? loadedCommentary;

  // Param-only navigation keeps the component mounted; per-row state must not
  // leak from the previous row into a save against the new one.
  useEffect(() => {
    setSavedCommentary(null);
    setCreatedContent(null);
    setManualTabsOpened(new Set());
  }, [editionId, bpaId, contactUuid]);

  const form = useForm<OrgFormValues>({
    resolver: orgFormResolver,
    defaultValues: EMPTY_ORG_FORM,
  });

  const draftsByTable = useRef(new Map<string, OrgFormValues>());

  useEffect(() => {
    if (!detail && !hasNoContent) return;

    const stashed = rankingTableId ? draftsByTable.current.get(rankingTableId) : undefined;
    if (stashed) {
      form.reset(stashed);
      setIsHydrated(true);
      return;
    }
    // A refetch lands query by query; hydrating from a half-refreshed snapshot shows stale data.
    if (isFetchingDetail || isFetchingSars || isFetchingRankings || isFetchingCommentary) return;

    const values = detail
      ? toOrgFormValues(detail, loadedCommentary, recordedSarsScores)
      : { ...EMPTY_ORG_FORM, ...toCommentaryFormValues(loadedCommentary) };
    for (const slider of sarsSliders) {
      if (!(slider.scoreKey in values.sarScores)) {
        values.sarScores[slider.scoreKey] = null;
      }
    }
    // Translations load on their own query; a reset here must carry what that query already
    // holds, or a refetch of the row blanks the translated column.
    const cachedTranslations = detail
      ? queryClient.getQueryData<TranslationsView>(["research-translations", detail.id])
      : undefined;
    form.reset({
      ...values,
      rankings: lawyerRankingsMap ?? EMPTY_ORG_FORM.rankings,
      translations: toTranslationsFormValues(cachedTranslations ?? null),
    });
    setIsHydrated(true);
  }, [
    detail,
    loadedCommentary,
    hasNoContent,
    recordedSarsScores,
    sarsSliders,
    lawyerRankingsMap,
    rankingTableId,
    isFetchingDetail,
    isFetchingSars,
    isFetchingRankings,
    isFetchingCommentary,
    form,
    queryClient,
  ]);

  const { isDirty } = form.formState;
  const navGuard = useNavigationGuard(isDirty);

  const watched = form.watch();
  // Editorial-tab fields are off screen while the flag is off, so they leave the denominator too.
  const rankingEntries = Object.entries(watched.rankings ?? {});
  const rankingFieldCount = rankingEntries.length;
  const filledRankingCount = rankingEntries.filter(
    ([, r]) => r.tier !== null || r.ranked || (r.comment ?? "").trim() !== "",
  ).length;

  const aggregateContent = allContentRows ?? [];
  const aggregatePopulated = aggregateContent.reduce((n, row) => n + (row.populatedFields ?? 0), 0);
  const aggregateTotal = aggregateContent.reduce((n, row) => n + (row.totalFields ?? 0), 0);
  const templateFieldTotal = submissionFields.length + sarsSliders.length;
  const manualTabsExtra = manualTabsOpened.size * templateFieldTotal;

  const filledFieldCount = aggregatePopulated + filledRankingCount;
  const totalFields = aggregateTotal + manualTabsExtra + rankingFieldCount;

  // Mirrors the backend's per-target status gate so a transition the actor lacks the
  // permission for is disabled up front rather than clicked into a 403.
  const canSetStatus = useCallback(
    (status: EditorialWriteStatus) => canSetReviewStatus(hasPermission, status),
    [hasPermission],
  );

  const translationsGateOpen =
    detail !== null && TRANSLATION_GATE_STATUSES.includes(detail.reviewStatus);
  const translationsQuery = useQuery({
    queryKey: ["research-translations", detail?.id],
    queryFn: () => researchCms.getTranslations(detail!.id),
    enabled: translationsEnabled && translationsGateOpen && activeTab === "translations",
  });
  const translationsView = translationsQuery.data ?? null;
  const [translationBusy, setTranslationBusy] = useState<string | null>(null);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const translationsDirty = Boolean(form.formState.dirtyFields.translations);

  // resetField only reaches fields the tab has registered, so this runs once the loaded view
  // has rendered its inputs; a full reset from the hydration effect covers the earlier window.
  useEffect(() => {
    if (!translationsQuery.data) return;
    form.resetField("translations", {
      defaultValue: toTranslationsFormValues(translationsQuery.data),
    });
  }, [translationsQuery.data, form]);

  async function runTranslate(target: TranslationTarget | "all") {
    if (!detail) return;
    setTranslationBusy(target === "all" || target === "commentary" ? target : target.matterId);
    setTranslationError(null);
    try {
      const body = target === "all" ? {} : { targets: [target], force: true };
      const result = await researchCms.translateContent(detail.id, body);
      queryClient.setQueryData(["research-translations", detail.id], result);
      form.resetField("translations", { defaultValue: toTranslationsFormValues(result) });
      const count = result.translated.length;
      setToast({
        message: `${count} field${count === 1 ? "" : "s"} translated, ${result.skipped.length} kept`,
        type: "success",
      });
    } catch (err) {
      setTranslationError(err instanceof Error ? err.message : "Translation failed");
    } finally {
      setTranslationBusy(null);
    }
  }

  function reasonText(reason: unknown): string {
    return reason instanceof Error ? reason.message : "unknown error";
  }

  // Null when the key is incomplete; callers decide whether that is an error or a skip.
  const upsertCommentary = (
    values: OrgFormValues,
    writeUp: EditorialContentDetail | null = detail,
  ) => {
    if (!editionBpaId || !companyIdForCommentary) return null;
    return withSaveTimeout(() =>
      researchCms
        .upsertEditorialCommentary(editionBpaId, {
          companyId: companyIdForCommentary,
          contactId: contactUuid,
          languageIso: commentaryLanguage(writeUp, publication),
          ...toCommentaryRequest(values),
        })
        .then(result => result.commentary),
    );
  };

  // Save asks before it writes: partial SARs scores trigger a confirmation modal.
  // Cancelling leaves the form unsaved and still guarded against navigation.
  function onSubmit(values: OrgFormValues) {
    if (isBusy) return;
    form.clearErrors("rankings");
    const unsavable = findUnsavableRankingEdits(values.rankings);
    if (unsavable.length > 0) {
      for (const tableId of unsavable) {
        form.setError(`rankings.${tableId}.tier`, {
          type: "manual",
          message: UNSAVABLE_RANKING_MESSAGE,
        });
      }
      setActiveTab("rankings");
      return;
    }
    const firmNames = findFirmTagsToConfirm(commentary?.postContent, values.postContent);
    if (firmNames.length > 0) {
      setSaveError(null);
      setPendingValues(values);
      setTaggedFirmNames(firmNames);
      return;
    }
    confirmUnscoredThenSave(values);
  }

  function confirmTaggedFirmsSave() {
    const values = pendingValues;
    setTaggedFirmNames(null);
    setPendingValues(null);
    if (values) confirmUnscoredThenSave(values);
  }

  function cancelTaggedFirmsSave() {
    setTaggedFirmNames(null);
    setPendingValues(null);
  }

  function confirmUnscoredThenSave(values: OrgFormValues) {
    const plan = planSarsWrite(values.sarScores, sarsSliders);
    if (plan.kind === "partial") {
      setSaveError(null);
      setPendingValues(values);
      setUnscoredLabels(plan.missingLabels);
      return;
    }
    void performSave(values);
  }

  function confirmPartialSave() {
    const values = pendingValues;
    setUnscoredLabels(null);
    setPendingValues(null);
    if (values) void performSave(values);
  }

  function cancelPartialSave() {
    setUnscoredLabels(null);
    setPendingValues(null);
  }

  async function performSave(values: OrgFormValues) {
    if (isBusy || isDeleted) return;
    if (!canEdit) {
      setSaveError("You do not have permission to edit content.");
      return;
    }
    setSaveError(null);
    setUnscoredLabels(null);

    setIsSaving(true);
    try {
      if (!hasLawyerSubmission && hasNoContent) {
        const rankingSaves = Object.entries(values.rankings ?? {})
          .map(([tableId, r]) => {
            const shouldExist = r.ranked || r.tier !== null;
            if (r.rankingId && !shouldExist) {
              return () => researchCms.deleteLawyerRanking(r.rankingId!);
            }
            if (r.rankingId) {
              return () =>
                researchCms.updateLawyerRanking(r.rankingId!, {
                  rankingTier: r.tier,
                  status: r.status,
                  rankingComment: r.comment || null,
                  displayOrder: r.displayOrder,
                });
            }
            if (shouldExist) {
              return () =>
                researchCms.createLawyerRanking(tableId, {
                  contactId: contactUuid,
                  contactName: lawyerName,
                  ...rankingFirmSnapshot,
                  rankingTier: r.tier ?? undefined,
                  status: r.status,
                  rankingComment: r.comment || undefined,
                  displayOrder: r.displayOrder,
                });
            }
            return null;
          })
          .filter(Boolean) as Array<() => Promise<unknown>>;

        if (rankingSaves.length > 0) {
          const results = await Promise.allSettled(rankingSaves.map(fn => fn()));
          const failures: string[] = [];
          for (const result of results) {
            if (result.status === "rejected") {
              failures.push(reasonText(result.reason));
            }
          }
          if (failures.length > 0) {
            setSaveError(`Some ranking changes failed to save — ${failures.join("; ")}`);
            return;
          }
        }

        if (hasCommentaryContent(values) || commentary !== null) {
          if (!editionBpaId || !companyIdForCommentary) {
            setSaveError(NO_EDITION_BPA_MESSAGE);
            return;
          }
          const saved = await upsertCommentary(values);
          if (saved) setSavedCommentary(saved);
        }

        form.reset(values);
        if (rankingSaves.length > 0) {
          void refetchLawyerRankings();
          void queryClient.invalidateQueries({
            queryKey: ["research-ranking-table-entries"],
          });
        }
        void queryClient.invalidateQueries({
          queryKey: ["research-lawyer-list", editionId, bpaId],
        });
        if (rankingTableId) draftsByTable.current.delete(rankingTableId);
        setToast({ message: "Saved", type: "success" });
        return;
      }

      let target: EditorialContentDetail;
      let contentValues: OrgFormValues;
      let sarsPlan: SarsWritePlan;

      if (hasNoContent) {
        if (!canCreateManualEntry) return;
        if (hasCommentaryContent(values) && !hasSubmissionContent(values)) {
          if (!editionBpaId || !companyIdForCommentary) {
            setSaveError(NO_EDITION_BPA_MESSAGE);
            return;
          }
          const saved = await upsertCommentary(values);
          if (saved) setSavedCommentary(saved);
          form.reset(values);
          setToast({ message: "Saved", type: "success" });
          return;
        }
        if (!firmCreateResult) return;
        if (!firmCreateResult.ok) {
          setSaveError(firmCreateResult.reason);
          return;
        }
        // The create endpoint takes no clients/contacts/matters; typed content only lands
        // via the update that follows. Retry patches the already-created row.
        target = createdContent ?? (await createOrAdoptLawyerContent(firmCreateResult.request));
        setCreatedContent(target);
        // A conflict may have adopted a pre-filled row; fields the form left empty must
        // carry that row's values into the PATCH, or the save wipes them.
        contentValues = mergeAdoptedValues(values, target);
        // Same for the row's own recorded scores: they — not just this save's form — decide
        // what gets written here.
        const recorded = (await researchCms.getSars(target.id)).sars;
        sarsPlan = planSarsWrite(mergeSarScores(values.sarScores, recorded), sarsSliders);
      } else {
        if (!detail) return;
        target = detail;
        contentValues = values;
        sarsPlan = planSarsWrite(values.sarScores, sarsSliders);
      }

      // One Save, five destinations: content PATCH, commentary upsert, SARs PUT, rankings PUT,
      // translations PUT. allSettled so one failure doesn't hide another's success.
      const wantsCommentary = commentary !== null || hasCommentaryContent(values);
      const sarsBody =
        sarsPlan.kind === "partial" || sarsPlan.kind === "complete" ? sarsPlan.body : null;

      const rankingSaves = Object.entries(contentValues.rankings ?? {})
        .map(([tableId, r]) => {
          const shouldExist = r.ranked || r.tier !== null;
          if (r.rankingId && !shouldExist) {
            return () => researchCms.deleteLawyerRanking(r.rankingId!);
          }
          if (r.rankingId) {
            return () =>
              researchCms.updateLawyerRanking(r.rankingId!, {
                rankingTier: r.tier,
                status: r.status,
                rankingComment: r.comment || null,
                displayOrder: r.displayOrder,
              });
          }
          if (shouldExist) {
            return () =>
              researchCms.createLawyerRanking(tableId, {
                contactId: contactUuid,
                contactName: lawyerName,
                ...rankingFirmSnapshot,
                rankingTier: r.tier ?? undefined,
                status: r.status,
                rankingComment: r.comment || undefined,
                displayOrder: r.displayOrder,
              });
          }
          return null;
        })
        .filter(Boolean) as Array<() => Promise<unknown>>;

      const translationsBody =
        !hasNoContent && translationsEnabled
          ? translationsSaveBody(values.translations, form.formState.dirtyFields.translations ?? {})
          : null;

      const [contentResult, commentaryResult, sarsResult, rankingsResult, translationsResult] =
        await Promise.allSettled([
          withSaveTimeout(() =>
            researchCms.updateEditorialContent(target.id, {
              ...toUpdateRequest(contentValues, target),
              ...rankingTableStamp(rankingTableId, target),
            }),
          ),
          wantsCommentary
            ? (upsertCommentary(values, target) ?? Promise.resolve(null))
            : Promise.resolve(null),
          sarsBody
            ? withSaveTimeout(() => researchCms.putSars(target.id, sarsBody))
            : Promise.resolve(null),
          rankingSaves.length > 0
            ? Promise.all(rankingSaves.map(fn => fn()))
            : Promise.resolve(null),
          translationsBody
            ? withSaveTimeout(() => researchCms.saveTranslations(target.id, translationsBody))
            : Promise.resolve(null),
        ]);

      if (commentaryResult.status === "fulfilled" && commentaryResult.value) {
        setSavedCommentary(commentaryResult.value);
      }
      if (translationsResult.status === "fulfilled" && translationsResult.value) {
        queryClient.setQueryData(["research-translations", target.id], translationsResult.value);
        contentValues = {
          ...contentValues,
          translations: toTranslationsFormValues(translationsResult.value),
        };
      }

      const failures: string[] = [];
      if (contentResult.status === "rejected") {
        failures.push(`submission fields: ${reasonText(contentResult.reason)}`);
      }
      if (commentaryResult.status === "rejected") {
        failures.push(`editorial commentary: ${reasonText(commentaryResult.reason)}`);
      }
      if (wantsCommentary && (!editionBpaId || !companyIdForCommentary)) {
        failures.push("editorial commentary: this practice area is not linked to the edition");
      }
      if (sarsResult.status === "rejected") {
        failures.push(`SARs scores: ${reasonText(sarsResult.reason)}`);
      }
      if (rankingsResult.status === "rejected") {
        failures.push(`rankings: ${reasonText(rankingsResult.reason)}`);
      }
      if (translationsResult.status === "rejected") {
        failures.push(`translations: ${reasonText(translationsResult.reason)}`);
      }
      if (failures.length > 0) {
        setSaveError(`Some changes failed to save — ${failures.join("; ")}`);
        return;
      }

      // Reset with merged values before the refresh, so a failed refresh doesn't leave the
      // form dirty; refresh errors report through `error`, not as a failed save.
      form.reset(contentValues);
      // Rankings are their own query too; refetched in the same batch, or the hydration effect
      // sees fresh content beside the cached tiers.
      await Promise.all([
        refetchContentAndScores(),
        rankingSaves.length > 0 ? refetchLawyerRankings() : null,
      ]);
      if (rankingSaves.length > 0) {
        void queryClient.invalidateQueries({
          queryKey: ["research-ranking-table-entries"],
        });
      }
      void queryClient.invalidateQueries({
        queryKey: ["research-lawyer-list", editionId, bpaId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["research-individual-all-content", editionId, bpaId, contactUuid],
      });
      // English edits change the source column and stale flags the translations view carries.
      if (translationsEnabled) {
        void queryClient.invalidateQueries({ queryKey: ["research-translations", target.id] });
      }
      if (rankingTableId) draftsByTable.current.delete(rankingTableId);
      setToast({ message: "Saved", type: "success" });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleStatusChange(nextStatus: EditorialWriteStatus) {
    // isDirty: the refetch below re-hydrates the form, dropping unsaved edits without a prompt.
    if (!detail || !canEdit || isBusy || isDirty) return;
    setSaveError(null);
    setIsTransitioning(true);
    try {
      await withSaveTimeout(() =>
        researchCms.updateEditorialContent(detail.id, {
          reviewStatus: nextStatus,
        }),
      );
      await refetch();
      setToast({
        message: STATUS_CHANGE_MESSAGES[nextStatus],
        type: "success",
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to change status");
    } finally {
      setIsTransitioning(false);
    }
  }

  function handleDiscard() {
    if (window.confirm("Are you sure? Unsaved changes will be lost.")) {
      form.reset();
      setSaveError(null);
    }
  }

  if (!hasPermission("staff.research.read")) return <UnauthorizedView />;

  if (!hasNavState && (apiLoading || rankingTablesLoading)) {
    return (
      <CmsLayout
        breadcrumbs={[
          {
            label: "Loading...",
            path: ROUTES.research.cmsPublication(editionId),
          },
        ]}
      >
        <PageHeader title="Loading..." subtitle="" />
      </CmsLayout>
    );
  }

  if (error && !hasLoaded) {
    return (
      <CmsLayout
        breadcrumbs={[
          {
            label: editionName,
            path: ROUTES.research.cmsPublication(editionId),
          },
        ]}
      >
        <PageHeader title="Failed to load individual" subtitle="" />
        <div className={styles.errorMessage} role="alert">
          {error.message}
        </div>
      </CmsLayout>
    );
  }

  const showFooter = !isReadOnly && canEdit;
  const statusActionsBlocked = isDirty && canEdit && detail !== null && !isDeleted;
  const inlineError = saveError ?? (error ? `Could not refresh this page: ${error.message}` : null);

  const sourcePanelSource: SourceDataSource = !companyIdForCommentary
    ? { kind: "known", docParseResultId: null }
    : rankingTablesFailed || firmContentRowsFailed
      ? { kind: "error" }
      : firmTable.ready && !firmContentRowsPending
        ? {
            kind: "lookup",
            lookup: {
              editionId,
              bpaId,
              companyUuid: companyIdForCommentary,
              rankingTableId: firmTable.rankingTableId,
            },
          }
        : { kind: "pending" };

  const sourceDataScopeFirm = individualSourceDataFirm({
    companyId: companyIdForCommentary,
    detailSettled: data !== undefined || error !== null,
    firmLookupPending: hasNoContent && !firmLookup && !firmLookupError,
  });

  return (
    <CmsLayout
      aside={
        hasFlag("research-cms-1-enabled") ? (
          <SourceDataPanel
            title={detail?.firmName || firmLookup?.companyName || organisationName || ""}
            source={sourcePanelSource}
            templateType={effectiveTemplateType}
            firm={sourceDataScopeFirm}
            editionId={editionId}
            researchBpaId={bpaId}
            names={{ editionName, rankingEntityName, bpaName }}
          />
        ) : undefined
      }
      breadcrumbs={[
        {
          label: editionName,
          path: ROUTES.research.cmsPublication(editionId),
        },
        // Links back to this country's practice areas, but only once the id is known — a direct
        // load before the BPA read lands has the name alone, and a link without it goes nowhere.
        ...(rankingEntityName
          ? [
              {
                label: rankingEntityName,
                ...(rankingEntityId
                  ? {
                      path: `${ROUTES.research.cmsPublication(editionId)}?rankingEntity=${rankingEntityId}`,
                    }
                  : {}),
              },
            ]
          : []),
        { label: bpaName, path: ROUTES.research.cmsBpa(editionId, bpaId) },
        { label: lawyerName },
      ]}
    >
      <IndividualBanner
        lawyerName={lawyerName}
        contactUuid={contactUuid}
        hasSubmission={detail?.docParseResultId != null}
        fileUri={fileUrlData?.fullUri ?? null}
        reviewStatus={detail?.reviewStatus ?? null}
        onStatusChange={canEdit ? handleStatusChange : undefined}
        canSetStatus={canSetStatus}
        busy={isBusy}
        statusActionsDisabled={statusActionsBlocked}
        showTranslationStep={needsTranslation}
        editionStatus={editionStatus}
        organisationName={detail?.firmName ?? firmLookup?.companyName}
        organisationUuid={companyIdForCommentary ?? undefined}
        officeId={detail?.officeId}
        officeName={officeData?.name}
        templateType={navState?.templateType}
      />

      <div className={styles.tabBar} role="tablist" aria-label="Individual sections">
        {TABS.filter(tab => {
          if (tab.id === "editorial" && !hasLawyerSubmission) return false;
          if (tab.id === "submission" && !hasLawyerSubmission) return false;
          if (tab.id === "translations" && !translationsEnabled) return false;
          return true;
        }).map(tab => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ""}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            id={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {hasLawyerSubmission && (
        <FormProgressBar filled={filledFieldCount} total={totalFields} isDirty={isDirty} />
      )}

      {hasLawyerSubmission && firmCreateResult && !firmCreateResult.ok && (
        <div className={shared.notice} role="status">
          <p className={shared.noticeMessage}>{firmCreateResult.reason}</p>
        </div>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className={styles.form}>
        <fieldset
          className={styles.formFields}
          disabled={hasLawyerSubmission ? isReadOnly : !canEdit}
          aria-label={
            hasLawyerSubmission
              ? isReadOnly
                ? "Editorial content (read-only)"
                : "Editorial content"
              : !canEdit
                ? "Rankings (read-only)"
                : "Rankings"
          }
        >
          {isHydrated ? (
            <div className={`${styles.tabContent} ${shared.fadeIn}`}>
              {hasLawyerSubmission && (
                <div
                  id="panel-submission"
                  role="tabpanel"
                  aria-labelledby="tab-submission"
                  style={{
                    display: activeTab === "submission" ? "block" : "none",
                  }}
                >
                  {lawyerSubmissionTables.length > 1 && (
                    <div
                      className={styles.subTabBar}
                      role="tablist"
                      aria-label="Ranking table tabs"
                    >
                      {lawyerSubmissionTables.map(table => (
                        <button
                          key={table.id}
                          type="button"
                          role="tab"
                          className={`${styles.subTab} ${effectiveTableId === table.id ? styles.subTabActive : ""}`}
                          aria-selected={effectiveTableId === table.id}
                          disabled={isBusy}
                          onClick={() => {
                            if (effectiveTableId) {
                              draftsByTable.current.set(effectiveTableId, form.getValues());
                            }
                            setSelectedTableId(table.id);
                          }}
                        >
                          {table.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {detail && hasFlag("research-cms-1-enabled") && (
                    <AutoTranslatedNotice
                      detectedLanguage={detail.detectedLanguage}
                      languageIso={detail.languageIso}
                    />
                  )}
                  {hasNoContent && !manualTabsOpened.has(rankingTableId ?? "") ? (
                    <div className={styles.noSubmission}>
                      <p className={styles.noSubmissionText}>
                        No submission for this ranking table.
                      </p>
                      <button
                        type="button"
                        className={styles.noSubmissionButton}
                        onClick={() => {
                          setManualTabsOpened(prev => new Set([...prev, rankingTableId ?? ""]));
                        }}
                      >
                        Create manual entry
                      </button>
                    </div>
                  ) : templateFieldsPending ? (
                    <CmsEmptyState message="Loading editorial content" loading />
                  ) : (
                    <TemplateSubmissionTab
                      control={form.control}
                      register={form.register}
                      watch={form.watch}
                      setValue={form.setValue}
                      errors={form.formState.errors}
                      fields={submissionFields}
                      sliders={sarsSliders}
                      templateType={effectiveTemplateType}
                    />
                  )}
                </div>
              )}

              {hasLawyerSubmission && (
                <div
                  id="panel-editorial"
                  role="tabpanel"
                  aria-labelledby="tab-editorial"
                  style={{
                    display: activeTab === "editorial" ? "block" : "none",
                  }}
                >
                  <EditorialTab
                    control={form.control}
                    register={form.register}
                    showClerksCommentary={showClerksCommentary}
                  />
                </div>
              )}

              <div
                id="panel-translations"
                role="tabpanel"
                aria-labelledby="tab-translations"
                style={{ display: activeTab === "translations" ? "block" : "none" }}
              >
                {translationsEnabled && (
                  <TranslationsTab
                    view={translationsView}
                    loading={translationsQuery.isLoading}
                    errorMessage={translationError ?? translationsQuery.error?.message ?? null}
                    gateMessage={translationsGateOpen ? null : GATE_MESSAGE}
                    autoPopulated={
                      detail?.detectedLanguage != null &&
                      detail.detectedLanguage === publication?.outputLanguage &&
                      detail.detectedLanguage !== detail.languageIso
                    }
                    canEdit={canEdit && !isDeleted}
                    dirty={translationsDirty}
                    busyTarget={translationBusy}
                    control={form.control}
                    register={form.register}
                    onTranslateAll={() => runTranslate("all")}
                    onRegenerate={runTranslate}
                  />
                )}
              </div>

              <div
                id="panel-details"
                role="tabpanel"
                aria-labelledby="tab-details"
                style={{
                  display: activeTab === "details" ? "block" : "none",
                }}
              >
                {detail ? (
                  hasLawyerSubmission ? (
                    <IndividualDetailsTab
                      content={detail}
                      ranking={
                        rankingTableId
                          ? {
                              rankingTableId,
                              rankingTableName: selectedTableName,
                              rankingId: scopedRankingId,
                            }
                          : undefined
                      }
                    />
                  ) : (
                    <IndividualDetailsTab content={detail} rankings={rankingsMetadata} />
                  )
                ) : (
                  <IndividualDetailsTab
                    content={stubContentForDetails}
                    rankings={rankingsMetadata}
                  />
                )}
              </div>

              <div
                id="panel-rankings"
                role="tabpanel"
                aria-labelledby="tab-rankings"
                style={{ display: activeTab === "rankings" ? "block" : "none" }}
              >
                <RankingsTab
                  tables={rankingTablesData?.rankingTables ?? []}
                  scopedTableId={undefined}
                  entityType="lawyer"
                  canEdit={canEdit}
                  canApprove={canApprove}
                  control={form.control}
                  previousTiers={previousTiers}
                  editionId={editionId}
                  bpaId={bpaId}
                />
              </div>
            </div>
          ) : (
            <CmsEmptyState message="Loading editorial content" loading />
          )}
        </fieldset>

        {inlineError && (
          <div className={styles.saveError} role="alert">
            {inlineError}
          </div>
        )}

        {(hasLawyerSubmission ? showFooter : canEdit) && (
          <div className={shared.footer}>
            {isDirty && (
              <button type="button" className={shared.discardButton} onClick={handleDiscard}>
                Discard changes
              </button>
            )}
            <button type="submit" className={shared.saveButton} disabled={isBusy}>
              {isSaving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </form>

      {navGuard.blocked && (
        <UnsavedChangesModal
          onCancel={navGuard.cancel}
          onDiscard={() => {
            form.reset();
            navGuard.proceed();
          }}
        />
      )}

      {taggedFirmNames && (
        <MultipleFirmTagsModal
          firmNames={taggedFirmNames}
          onConfirm={confirmTaggedFirmsSave}
          onCancel={cancelTaggedFirmsSave}
        />
      )}

      {unscoredLabels && (
        <UnscoredQuestionsModal
          missingLabels={unscoredLabels}
          onConfirm={confirmPartialSave}
          onCancel={cancelPartialSave}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </CmsLayout>
  );
}
